import React, { useState, useRef } from 'react';
import { Printer, ShoppingCart } from 'lucide-react';
import { toast } from 'sonner';
import fetchWithAuth from '../../services/fetchWithAuth';

const API = process.env.REACT_APP_BACKEND_URL;
const getToken = () => localStorage.getItem('access_token');

const formatINR = (v) => v != null ? Number(v).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0.00';
const formatWt = (kg) => {
  if (!kg || kg === 0) return '—';
  if (kg >= 1) return `${kg.toFixed(3)} kg`;
  return `${(kg * 1000).toFixed(1)} g`;
};

const s = {
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 4 },
  h1: { fontSize: 16, fontWeight: 700, margin: 0, color: '#222' },
  subtitle: { fontSize: 11, color: '#666', marginTop: 4 },
  meta: { textAlign: 'right', fontSize: 11, color: '#666', lineHeight: 1.7 },
  bold: { fontWeight: 700 },
  sep: { borderTop: '1px solid #ccc', marginTop: 10, marginBottom: 14 },
  row: { display: 'flex', gap: 10, marginBottom: 14 },
  card: { flex: 1, border: '1px solid #ddd', padding: '7px 10px' },
  cardLabel: { fontSize: 8, textTransform: 'uppercase', letterSpacing: 0.5, color: '#666', marginBottom: 1 },
  cardValue: { fontSize: 13, fontWeight: 700, color: '#222' },
  cardPct: { fontSize: 9, color: '#888', marginTop: 1 },
  driverBox: { background: '#fff8e1', border: '1px solid #f59e0b', padding: '8px 10px', marginBottom: 14 },
  driverTitle: { fontSize: 10, fontWeight: 700, textTransform: 'uppercase', color: '#b45309', marginBottom: 4 },
  driverLine: { display: 'flex', justifyContent: 'space-between', fontSize: 11, color: '#222', padding: '1px 0' },
  driverRank: { color: '#b45309', fontWeight: 700, marginRight: 6 },
  sectionHdr: { fontSize: 13, fontWeight: 700, color: '#222', marginTop: 16, marginBottom: 2, borderBottom: '2px solid #222', paddingBottom: 4, display: 'flex', justifyContent: 'space-between' },
  formHdr: { background: '#e8f0f8', padding: '4px 8px', fontWeight: 700, fontSize: 12, borderBottom: '1px solid #ccc', display: 'flex', justifyContent: 'space-between', marginTop: 8 },
  subgroupHdr: { background: '#e8f0f8', padding: '4px 8px', fontWeight: 700, fontSize: 12, borderBottom: '1px solid #ccc', display: 'flex', justifyContent: 'space-between', marginTop: 8 },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 10 },
  th: { background: '#f5f5f5', textAlign: 'left', padding: '3px 6px', border: '1px solid #ddd', fontWeight: 700, fontSize: 9, textTransform: 'uppercase' },
  thR: { background: '#f5f5f5', textAlign: 'right', padding: '3px 6px', border: '1px solid #ddd', fontWeight: 700, fontSize: 9, textTransform: 'uppercase' },
  td: { padding: '3px 6px', border: '1px solid #ddd', color: '#222' },
  tdR: { padding: '3px 6px', border: '1px solid #ddd', textAlign: 'right', color: '#222' },
  tdBold: { padding: '3px 6px', border: '1px solid #ddd', fontWeight: 700, color: '#222' },
  subRow: { fontWeight: 700, background: '#f5f5f5' },
  totalTd: { fontWeight: 700, background: '#e8e8e8', padding: '4px 6px', border: '1px solid #bbb', color: '#222' },
  totalTdR: { fontWeight: 700, background: '#e8e8e8', padding: '4px 6px', border: '1px solid #bbb', textAlign: 'right', color: '#222' },
  footer: { marginTop: 20, borderTop: '1px solid #ccc', paddingTop: 10, display: 'flex', justifyContent: 'space-between', fontSize: 10, color: '#666' },
  pctBar: { display: 'inline-block', height: 8, background: '#3b82f6', borderRadius: 1, marginRight: 4, verticalAlign: 'middle' },
};

const PctCell = ({ pct }) => (
  <td style={s.tdR}>
    <span style={{ ...s.pctBar, width: Math.max(1, Math.min(pct * 0.6, 40)) }} />
    {pct}%
  </td>
);

const PrintOverlay = ({ data, onClose }) => {
  const printRef = useRef(null);
  const today = new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });

  const handlePrint = () => {
    const content = printRef.current;
    if (!content) return;
    const w = window.open('', '_blank', 'width=960,height=700');
    w.document.write(`<!DOCTYPE html><html><head><title>Purchase BOM</title>
<style>* { margin: 0; padding: 0; box-sizing: border-box; } body { font-family: monospace; color: #222; padding: 24px; font-size: 12px; } @media print { body { padding: 12px; } }</style>
</head><body>${content.innerHTML}</body></html>`);
    w.document.close();
    setTimeout(() => { w.print(); }, 300);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-white rounded-lg w-[940px] max-h-[90vh] overflow-hidden shadow-2xl" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-2.5 bg-slate-100 border-b">
          <span className="text-sm font-semibold text-slate-800">Purchase Bill of Materials — Print Preview</span>
          <div className="flex gap-2">
            <button onClick={handlePrint} data-testid="purchase-bom-print-btn"
              className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-900 text-white rounded text-xs font-medium hover:bg-slate-700 transition-colors">
              <Printer className="w-3.5 h-3.5" /> Print
            </button>
            <button onClick={onClose}
              className="px-3 py-1.5 bg-slate-200 text-slate-700 rounded text-xs font-medium hover:bg-slate-300 transition-colors">
              Close
            </button>
          </div>
        </div>
        <div className="overflow-y-auto max-h-[calc(90vh-48px)] p-5 bg-white">
          <div ref={printRef} style={{ fontFamily: 'monospace', fontSize: 12, color: '#222' }}>
            {/* Header */}
            <div style={s.header}>
              <div>
                <h1 style={s.h1}>PURCHASE BILL OF MATERIALS</h1>
                <div style={s.subtitle}>{data.bom_number} {data.bom_name}</div>
              </div>
              <div style={s.meta}>
                <div>Revision: <span style={s.bold}>{data.revision_label}</span></div>
                <div>Date: {today}</div>
                <div>Build Qty: {data.build_qty}</div>
              </div>
            </div>
            <div style={s.sep} />

            {/* Summary Cards */}
            <div style={s.row}>
              <div style={s.card}>
                <div style={s.cardLabel}>Raw Materials</div>
                <div style={s.cardValue}>{'\u20B9'}{formatINR(data.raw_material_total)}</div>
                <div style={s.cardPct}>{data.raw_material_pct}% of BOM</div>
              </div>
              <div style={s.card}>
                <div style={s.cardLabel}>Purchased Parts</div>
                <div style={s.cardValue}>{'\u20B9'}{formatINR(data.buy_total)}</div>
                <div style={s.cardPct}>{data.buy_pct}% of BOM</div>
              </div>
              <div style={s.card}>
                <div style={s.cardLabel}>Consumables</div>
                <div style={s.cardValue}>{'\u20B9'}{formatINR(data.consumables_total)}</div>
                <div style={s.cardPct}>{data.consumables_pct}% of BOM</div>
              </div>
              <div style={{ ...s.card, background: '#f0f0f0' }}>
                <div style={s.cardLabel}>Total BOM Cost</div>
                <div style={s.cardValue}>{'\u20B9'}{formatINR(data.total_bom_cost)}</div>
                <div style={s.cardPct}>Weight: {formatWt(data.total_bom_weight_kg)}</div>
              </div>
            </div>

            {/* Top Cost Drivers */}
            <div style={s.driverBox}>
              <div style={s.driverTitle}>Top Cost Drivers</div>
              {data.top_cost_drivers.map((d, i) => (
                <div key={i} style={s.driverLine}>
                  <span><span style={s.driverRank}>#{i + 1}</span> {d.name} <span style={{ color: '#888', fontSize: 9 }}>({d.type})</span></span>
                  <span><span style={s.bold}>{'\u20B9'}{formatINR(d.cost)}</span> <span style={{ color: '#888' }}>{d.pct}%</span></span>
                </div>
              ))}
            </div>

            {/* Section 1: Raw Materials */}
            <div style={s.sectionHdr}>
              <span>SECTION 1: RAW MATERIALS</span>
              <span>{'\u20B9'}{formatINR(data.raw_material_total)} ({data.raw_material_pct}%)</span>
            </div>
            {data.raw_material_sections.map(sect => (
              <div key={sect.form}>
                <div style={s.formHdr}>
                  <span>{sect.form}</span>
                  <span>{'\u20B9'}{formatINR(sect.subtotal_cost)} &middot; {formatWt(sect.subtotal_weight_kg)}</span>
                </div>
                <table style={s.table}>
                  <thead>
                    <tr>
                      <th style={s.th}>Material Spec</th>
                      <th style={{ ...s.th, width: 50 }}>Grade</th>
                      <th style={{ ...s.thR, width: 50 }}>Net Req</th>
                      <th style={{ ...s.th, width: 30 }}>Unit</th>
                      <th style={{ ...s.thR, width: 38 }}>Kerf</th>
                      <th style={{ ...s.th, width: 75 }}>Order Qty</th>
                      <th style={{ ...s.thR, width: 65 }}>Rate/Unit</th>
                      <th style={{ ...s.thR, width: 68 }}>Amount</th>
                      <th style={{ ...s.thR, width: 45 }}>%Sect</th>
                      <th style={{ ...s.thR, width: 40 }}>%BOM</th>
                      <th style={{ ...s.thR, width: 50 }}>Weight</th>
                      <th style={{ ...s.th, width: 55 }}>Used By</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sect.items.map((item, idx) => (
                      <tr key={idx}>
                        <td style={s.tdBold}>{item.profile_name}<br /><span style={{ fontWeight: 400, fontSize: 9, color: '#666' }}>{item.profile_code}</span></td>
                        <td style={s.td}>{item.material_grade}</td>
                        <td style={s.tdR}>{item.net_qty}</td>
                        <td style={s.td}>{item.unit}</td>
                        <td style={s.tdR}>{item.kerf_mm > 0 ? `${item.kerf_mm}mm` : '—'}</td>
                        <td style={s.td}>{item.order_desc}</td>
                        <td style={s.tdR}>{'\u20B9'}{formatINR(item.rate_per_unit)}/{item.unit}</td>
                        <td style={s.tdR}>{'\u20B9'}{formatINR(item.material_cost)}</td>
                        <PctCell pct={item.pct_of_section} />
                        <PctCell pct={item.pct_of_bom} />
                        <td style={s.tdR}>{formatWt(item.weight_kg)}</td>
                        <td style={{ ...s.td, fontSize: 9 }}>{item.used_by.join(', ')}</td>
                      </tr>
                    ))}
                    <tr style={s.subRow}>
                      <td style={s.totalTd} colSpan={7}>{sect.form} Subtotal</td>
                      <td style={s.totalTdR}>{'\u20B9'}{formatINR(sect.subtotal_cost)}</td>
                      <td style={s.totalTdR} colSpan={2}>{sect.pct_of_bom}% of BOM</td>
                      <td style={s.totalTdR}>{formatWt(sect.subtotal_weight_kg)}</td>
                      <td style={s.totalTd}></td>
                    </tr>
                  </tbody>
                </table>
              </div>
            ))}

            {/* Section 2: Purchased Parts */}
            <div style={s.sectionHdr}>
              <span>SECTION 2: PURCHASED PARTS</span>
              <span>{'\u20B9'}{formatINR(data.buy_total)} ({data.buy_pct}%)</span>
            </div>
            {data.buy_sections.map(sect => (
              <div key={sect.subgroup}>
                <div style={s.subgroupHdr}>
                  <span>{sect.subgroup}</span>
                  <span>{'\u20B9'}{formatINR(sect.subtotal_cost)} &middot; {formatWt(sect.subtotal_weight_kg)}</span>
                </div>
                <table style={s.table}>
                  <thead>
                    <tr>
                      <th style={{ ...s.th, width: 80 }}>Part #</th>
                      <th style={s.th}>Description</th>
                      <th style={{ ...s.thR, width: 35 }}>Qty</th>
                      <th style={{ ...s.thR, width: 65 }}>Unit Cost</th>
                      <th style={{ ...s.thR, width: 70 }}>Ext Cost</th>
                      <th style={{ ...s.thR, width: 45 }}>%Sect</th>
                      <th style={{ ...s.thR, width: 40 }}>%BOM</th>
                      <th style={{ ...s.thR, width: 55 }}>Weight</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sect.items.map((item, idx) => (
                      <tr key={idx}>
                        <td style={s.tdBold}>{item.part_number}</td>
                        <td style={s.td}>{item.description}</td>
                        <td style={s.tdR}>{item.quantity}</td>
                        <td style={s.tdR}>{'\u20B9'}{formatINR(item.unit_cost)}</td>
                        <td style={s.tdR}>{'\u20B9'}{formatINR(item.extended_cost)}</td>
                        <PctCell pct={item.pct_of_section} />
                        <PctCell pct={item.pct_of_bom} />
                        <td style={s.tdR}>{formatWt(item.weight_kg)}</td>
                      </tr>
                    ))}
                    <tr style={s.subRow}>
                      <td style={s.totalTd} colSpan={4}>{sect.subgroup} Subtotal</td>
                      <td style={s.totalTdR}>{'\u20B9'}{formatINR(sect.subtotal_cost)}</td>
                      <td style={s.totalTdR} colSpan={2}>{sect.pct_of_bom}% of BOM</td>
                      <td style={s.totalTdR}>{formatWt(sect.subtotal_weight_kg)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            ))}

            {/* Section 3: Process Consumables */}
            {data.consumables.length > 0 && (
              <>
                <div style={s.sectionHdr}>
                  <span>SECTION 3: PROCESS CONSUMABLES</span>
                  <span>{'\u20B9'}{formatINR(data.consumables_total)} ({data.consumables_pct}%)</span>
                </div>
                <table style={{ ...s.table, marginTop: 6 }}>
                  <thead>
                    <tr>
                      <th style={s.th}>Consumable</th>
                      <th style={{ ...s.th, width: 90 }}>Process</th>
                      <th style={{ ...s.thR, width: 60 }}>Total Qty</th>
                      <th style={{ ...s.th, width: 30 }}>Unit</th>
                      <th style={{ ...s.thR, width: 70 }}>Total Cost</th>
                      <th style={{ ...s.thR, width: 45 }}>%Sect</th>
                      <th style={{ ...s.thR, width: 40 }}>%BOM</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.consumables.map((c, idx) => (
                      <tr key={idx}>
                        <td style={s.tdBold}>{c.name}</td>
                        <td style={s.td}>{c.process_category}</td>
                        <td style={s.tdR}>{c.quantity.toFixed(4)}</td>
                        <td style={s.td}>{c.uom}</td>
                        <td style={s.tdR}>{'\u20B9'}{formatINR(c.cost)}</td>
                        <PctCell pct={c.pct_of_section} />
                        <PctCell pct={c.pct_of_bom} />
                      </tr>
                    ))}
                    <tr style={s.subRow}>
                      <td style={s.totalTd} colSpan={4}>Consumables Total</td>
                      <td style={s.totalTdR}>{'\u20B9'}{formatINR(data.consumables_total)}</td>
                      <td style={s.totalTdR} colSpan={2}>{data.consumables_pct}% of BOM</td>
                    </tr>
                  </tbody>
                </table>
              </>
            )}

            {/* Grand Total */}
            <table style={{ ...s.table, marginTop: 16 }}>
              <tbody>
                <tr>
                  <td style={{ ...s.totalTd, fontSize: 12, borderTop: '2px solid #222', textAlign: 'right', paddingRight: 12 }} colSpan={4}>GRAND TOTAL</td>
                  <td style={{ ...s.totalTdR, fontSize: 12, borderTop: '2px solid #222', width: 90 }}>{'\u20B9'}{formatINR(data.total_bom_cost)}</td>
                  <td style={{ ...s.totalTdR, fontSize: 12, borderTop: '2px solid #222', width: 70 }}>{formatWt(data.total_bom_weight_kg)}</td>
                </tr>
              </tbody>
            </table>

            {/* Footer */}
            <div style={s.footer}>
              <span>Generated from HADEN ATLAS PLM</span>
              <span>Printed: {today}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export const PurchaseBomButton = ({ bomId, revisionId }) => {
  const [showPreview, setShowPreview] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleClick = async () => {
    setLoading(true);
    try {
      const res = await fetchWithAuth(
        `${API}/api/v1/bom/${bomId}/revisions/${revisionId}/purchase-bom`,
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      if (!res.ok) {
        const err = await res.json().catch(() => null);
        throw new Error(err?.detail || 'Failed to generate Purchase BOM');
      }
      const d = await res.json();
      setData(d);
      setShowPreview(true);
    } catch (e) {
      toast.error(e.message);
    }
    setLoading(false);
  };

  return (
    <>
      <button onClick={handleClick} disabled={loading} data-testid="purchase-bom-btn"
        className="flex items-center gap-1 px-2 py-0.5 text-[10px] rounded transition-colors bg-slate-700/50 hover:bg-slate-600/50 text-slate-400 hover:text-white disabled:opacity-50">
        <ShoppingCart className="w-3 h-3" />{loading ? 'Loading...' : 'Purchase BOM'}
      </button>
      {showPreview && data && <PrintOverlay data={data} onClose={() => setShowPreview(false)} />}
    </>
  );
};
